import React, { useState, useEffect } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL || 'https://plasann-production.up.railway.app';

function Analysis() {
  // State for form inputs (like Streamlit)
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState("fasta");
  const [customName, setCustomName] = useState("");
  const [uniprotBlast, setUniprotBlast] = useState(false);
  const [minIdentity, setMinIdentity] = useState(50);
  const [processingMethod, setProcessingMethod] = useState("retain");
  
  // State for analysis
  const [status, setStatus] = useState("");
  const [jobId, setJobId] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [csvData, setCsvData] = useState(null);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(0);

  // Poll for status updates when job is running
  useEffect(() => {
    let interval;
    if (isRunning && jobId) {
      interval = setInterval(async () => {
        try {
          const response = await axios.get(`${API_BASE}/api/status/${jobId}`);
          const data = response.data;
          
          setStatus(data.message);
          
          // Update progress based on message content (like Streamlit)
          if (data.message.includes("Starting")) setProgress(10);
          else if (data.message.includes("Downloading database")) setProgress(20);
          else if (data.message.includes("Running")) setProgress(30);
          else if (data.message.includes("BLAST")) setProgress(50);
          else if (data.message.includes("origin")) setProgress(70);
          else if (data.message.includes("intergenic")) setProgress(85);
          else if (data.message.includes("map")) setProgress(95);
          
          if (data.status === "completed") {
            setIsRunning(false);
            setResults(data);
            setProgress(100);
            setStatus("✅ Analysis completed successfully!");
            
            // Load CSV data for display
            if (data.files && data.files.length > 0) {
              loadCsvData(jobId);
            }
            
          } else if (data.status === "error") {
            setIsRunning(false);
            setError(data.message);
            setStatus("❌ " + data.message);
            setProgress(0);
          }
        } catch (err) {
          console.error("Status check failed:", err);
        }
      }, 3000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunning, jobId]);

  const loadCsvData = async (jobId) => {
    try {
      const response = await axios.get(`${API_BASE}/api/csv/${jobId}`);
      setCsvData(response.data);
    } catch (err) {
      console.error("Failed to load CSV data:", err);
    }
  };

  // 🔥 FIX: Complete state reset function
  const resetAnalysisState = () => {
    setError("");
    setResults(null);
    setCsvData(null);
    setProgress(0);
    setStatus("");
    setJobId("");
    setIsRunning(false);
  };

  // 🔥 FIX: Handle file change with state reset
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    
    // Clear previous analysis results when new file selected
    resetAnalysisState();
    
    setFile(selectedFile);
  };

  // 📥 ENHANCED handleSubmit with retry logic for cold starts
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError("Please upload a file.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", fileType);
    formData.append("custom_name", customName);
    formData.append("uniprot_blast", uniprotBlast);
    formData.append("min_identity", minIdentity);
    formData.append("overwrite", processingMethod === "overwrite");

    // 🔥 FIX: Complete state reset BEFORE starting new job
    resetAnalysisState();
    setIsRunning(true);
    
    // 📥 RETRY LOGIC FOR COLD STARTS
    const maxRetries = 3;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        if (attempt === 1) {
          setStatus("🔄 Starting analysis...");
        } else {
          setStatus(`🔄 Server warming up (attempt ${attempt}/${maxRetries})...`);
        }
        
        const response = await axios.post(`${API_BASE}/api/analyze`, formData, {
          timeout: 60000, // 60 second timeout
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        });
        
        // SUCCESS! 
        if (response.data.job_id) {
          setJobId(response.data.job_id);
          setStatus("🔄 Analysis started, please wait...");
          return; // Exit retry loop - SUCCESS!
        } else {
          throw new Error("No job ID returned");
        }
        
      } catch (err) {
        console.error(`Analysis submission attempt ${attempt} failed:`, err);
        
        if (attempt < maxRetries) {
          // Wait before retry (exponential backoff)
          const delay = attempt * 5000; // 5s, 10s, 15s
          setStatus(`⏳ Retrying in ${delay/1000} seconds...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        } else {
          // Final failure after all retries
          let errorMessage = "Network error occurred";
          if (err.response?.data?.error) {
            errorMessage = err.response.data.error;
          } else if (err.code === 'ECONNABORTED') {
            errorMessage = "Request timeout - please try again";
          } else if (!err.response) {
            errorMessage = "Cannot connect to server - service may be starting up";
          }
          
          setError(errorMessage);
          setStatus("❌ Error: " + errorMessage);
          setIsRunning(false);
        }
      }
    }
  };

  const downloadFile = (filename) => {
    window.open(`${API_BASE}/api/file/${jobId}/${filename}`, '_blank');
  };

  const getFileType = (filename) => {
    if (filename.endsWith('.csv')) return '📊 Annotations CSV';
    if (filename.endsWith('.gbk')) return '📄 GenBank File';
    if (filename.endsWith('.png')) return '🎨 Plasmid Map';
    return '📄 File';
  };

  const validateFile = (file) => {
    if (!file) return { valid: false, message: "No file selected" };
    
    const name = file.name.toLowerCase();
    const fasta_exts = ['.fasta', '.fa', '.fsa', '.fna'];
    const genbank_exts = ['.gb', '.gbk', '.genbank'];
    
    if (fileType === "fasta") {
      const valid = fasta_exts.some(ext => name.endsWith(ext));
      return { 
        valid, 
        message: valid ? "Valid FASTA file" : "Please upload a FASTA file (.fasta, .fa, .fsa, .fna)" 
      };
    } else if (fileType === "genbank") {
      const valid = genbank_exts.some(ext => name.endsWith(ext));
      return { 
        valid, 
        message: valid ? "Valid GenBank file" : "Please upload a GenBank file (.gb, .gbk, .genbank)" 
      };
    }
    
    return { valid: true, message: "File appears valid" };
  };

  const fileValidation = file ? validateFile(file) : { valid: true, message: "" };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#354931] via-[#5d8156] to-[#86bb7c] text-white p-6 rounded-lg mb-6 shadow-lg">
        <h1 className="text-3xl font-bold mb-2">🧬 PlasAnn Analysis</h1>
        <p className="text-[#c8d3c5]">
          Upload your plasmid sequences and get comprehensive annotations!
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Panel - Configuration (like Streamlit sidebar) */}
        <div className="lg:col-span-1">
          <div className="bg-gradient-to-br from-white to-[#f7f9f7] shadow-lg rounded-lg p-6 border border-[#e1e8e1]">
            <h2 className="text-xl font-bold mb-4 bg-gradient-to-r from-[#4e5a4c] to-[#6b7966] bg-clip-text text-transparent">⚙️ Configuration</h2>
            
            {/* File Upload */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Choose a FASTA or GenBank file
              </label>
              <input 
                type="file" 
                accept=".fasta,.fa,.fsa,.fna,.gb,.gbk,.genbank"
                onChange={handleFileChange}
                disabled={isRunning}
                className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966] transition-colors"
              />
              {file && (
                <div className="mt-2 p-2 bg-gradient-to-r from-[#f1f4f1] to-[#e8efe8] rounded-lg">
                  <p className="text-sm text-[#4e5a4c]">📎 {file.name}</p>
                  <p className="text-sm text-[#6b7966]">📊 {(file.size / 1024).toFixed(1)} KB</p>
                  {fileValidation.message && (
                    <p className={`text-sm mt-1 ${fileValidation.valid ? 'text-green-600' : 'text-red-600'}`}>
                      {fileValidation.valid ? '✅' : '❌'} {fileValidation.message}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* File Type */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">File Type</label>
              <select 
                value={fileType} 
                onChange={(e) => setFileType(e.target.value)}
                disabled={isRunning}
                className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966] transition-colors bg-gradient-to-r from-white to-[#fafbfa]"
              >
                <option value="fasta">FASTA (.fasta, .fa, .fna)</option>
                <option value="genbank">GenBank (.gb, .gbk)</option>
              </select>
            </div>

            {/* Custom Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom Output Name (optional)
              </label>
              <input 
                type="text"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                disabled={isRunning}
                placeholder="Leave empty to use filename"
                className="w-full p-2 border border-[#d1ddd1] rounded focus:ring-2 focus:ring-[#6b7966] focus:border-[#6b7966] transition-colors bg-gradient-to-r from-white to-[#fafbfa]"
              />
              <p className="text-xs text-gray-500 mt-1">
                Custom name for output files (leave empty to use filename)
              </p>
            </div>

            {/* Advanced Options */}
            <div className="border-t border-[#e1e8e1] pt-4 mt-4 bg-gradient-to-br from-[#fafcfa] to-[#f5f8f5] rounded-lg p-3 -mx-3">
              <h3 className="text-lg font-semibold mb-3 bg-gradient-to-r from-[#5a6851] to-[#7a8577] bg-clip-text text-transparent">🔬 Advanced Options</h3>
              
              {/* UniProt BLAST */}
              <div className="mb-4">
                <label className="flex items-center space-x-2">
                  <input 
                    type="checkbox"
                    checked={uniprotBlast}
                    onChange={(e) => setUniprotBlast(e.target.checked)}
                    disabled={isRunning}
                    className="rounded border-gray-300 text-[#6b7966] focus:ring-[#6b7966]"
                  />
                  <span className="text-sm font-medium">🧬 Enhanced UniProt BLAST annotation</span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  Comprehensive protein annotation (slower but more detailed)
                </p>
                
                {uniprotBlast && (
                  <div className="ml-6 mt-3 p-3 bg-gradient-to-r from-[#f1f4f1] to-[#e8efe8] rounded-lg">
                    <label className="block text-sm font-medium text-[#4e5a4c] mb-2">
                      UniProt minimum identity: {minIdentity}%
                    </label>
                    <input 
                      type="range"
                      min="30"
                      max="90"
                      value={minIdentity}
                      onChange={(e) => setMinIdentity(parseInt(e.target.value))}
                      disabled={isRunning}
                      className="w-full accent-[#6b7966]"
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>30%</span>
                      <span>90%</span>
                    </div>
                  </div>
                )}
              </div>

              {/* GenBank Processing Method */}
              {fileType === "genbank" && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    🔄 GenBank Processing
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input 
                        type="radio"
                        name="processing"
                        value="retain"
                        checked={processingMethod === "retain"}
                        onChange={(e) => setProcessingMethod(e.target.value)}
                        disabled={isRunning}
                        className="text-[#6b7966] focus:ring-[#6b7966]"
                      />
                      <span className="text-sm">Retain original annotations</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input 
                        type="radio"
                        name="processing"
                        value="overwrite"
                        checked={processingMethod === "overwrite"}
                        onChange={(e) => setProcessingMethod(e.target.value)}
                        disabled={isRunning}
                        className="text-[#6b7966] focus:ring-[#6b7966]"
                      />
                      <span className="text-sm">Use Prodigal (overwrite)</span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Retain: Use existing GenBank annotations<br/>
                    Overwrite: Re-predict genes with Prodigal
                  </p>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button 
              onClick={handleSubmit}
              disabled={isRunning || !file || !fileValidation.valid}
              className="w-full bg-gradient-to-r from-[#6b7966] to-[#5a6851] text-white px-4 py-3 rounded-lg font-medium hover:from-[#5d6a5b] hover:to-[#4e5a4c] disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all duration-200 shadow-md"
            >
              {isRunning ? "🔬 Analyzing..." : "🚀 Start Analysis"}
            </button>
            
            {/* 🔥 NEW: Clear Results Button */}
            {(results || error) && !isRunning && (
              <button 
                onClick={resetAnalysisState}
                className="w-full mt-2 bg-gradient-to-r from-gray-500 to-gray-600 text-white px-4 py-2 rounded-lg font-medium hover:from-gray-600 hover:to-gray-700 transition-all duration-200"
              >
                🔄 Clear Results & Start New Analysis
              </button>
            )}
          </div>

          {/* System Status (like Streamlit) */}
          <div className="bg-gradient-to-br from-white to-[#f0f4f0] shadow-lg rounded-lg p-6 mt-6 border border-[#e1e8e1]">
            <h3 className="text-lg font-semibold mb-3 bg-gradient-to-r from-[#5a6851] to-[#7a8577] bg-clip-text text-transparent">ℹ️ System Status</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center space-x-2">
                <span className="text-green-600">✅</span>
                <span>Backend server running</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-green-600">✅</span>
                <span>PlasAnn modules available</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-yellow-600">🔥</span>
                <span>Database downloads automatically</span>
              </div>
            </div>
            
            {/* Cold start info */}
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-700">
                💡 <strong>Note:</strong> First request after idle time may take 30-60 seconds due to server startup. The system will automatically retry if needed.
              </p>
            </div>
          </div>
        </div>

        {/* Right Panel - Main Content */}
        <div className="lg:col-span-2">
          
          {/* Status and Progress */}
          {status && (
            <div className="bg-gradient-to-br from-white to-[#f7f9f7] shadow-lg rounded-lg p-6 mb-6 border border-[#e1e8e1]">
              <h3 className="text-lg font-semibold mb-3 bg-gradient-to-r from-[#4e5a4c] to-[#6b7966] bg-clip-text text-transparent">🔄 Analysis Progress</h3>
              
              {/* Progress Bar */}
              <div className="w-full bg-gradient-to-r from-gray-200 to-gray-300 rounded-full h-3 mb-4">
                <div 
                  className="bg-gradient-to-r from-[#6b7966] to-[#7a8577] h-3 rounded-full transition-all duration-300 ease-out shadow-sm"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              
              <div className="flex justify-between items-center text-sm text-gray-600 mb-2">
                <span>{progress}% complete</span>
              </div>
              
              <p className="text-sm text-gray-700">{status}</p>
              
              {error && (
                <div className="mt-4 p-3 bg-gradient-to-r from-red-50 to-red-100 border border-red-400 text-red-700 rounded">
                  <strong>Error:</strong> {error}
                  {error.includes("Network error") && (
                    <div className="mt-2 text-sm">
                      💡 This usually happens when the server is starting up. The system automatically retries.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Instructions (when no file uploaded) */}
          {!file && (
            <div className="bg-gradient-to-br from-white to-[#f7f9f7] shadow-lg rounded-lg p-6 border border-[#e1e8e1]">
              <h2 className="text-xl font-bold mb-4 bg-gradient-to-r from-[#4e5a4c] to-[#6b7966] bg-clip-text text-transparent">📖 How to Use PlasAnn</h2>
              <div className="space-y-4 text-sm">
                <div className="p-4 bg-gradient-to-r from-[#f1f4f1] to-[#e8efe8] rounded-lg">
                  <h3 className="font-semibold text-[#5a6851] mb-2">🚀 Quick Start</h3>
                  <ol className="list-decimal list-inside space-y-1 text-gray-700">
                    <li>Upload your plasmid file (FASTA or GenBank format)</li>
                    <li>Select file type (or use auto-detection)</li>
                    <li>Configure options (UniProt BLAST, GenBank processing)</li>
                    <li>Click "Start Analysis"</li>
                    <li>Wait for processing (5-15 minutes depending on options)</li>
                    <li>Download your results!</li>
                  </ol>
                </div>
                
                <div className="p-4 bg-gradient-to-r from-[#e8efe8] to-[#dae7da] rounded-lg">
                  <h3 className="font-semibold text-[#5a6851] mb-2">📊 What You Get</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    <li><strong>CSV file:</strong> Detailed annotations for all features</li>
                    <li><strong>GenBank file:</strong> Standardized format for other tools</li>
                    <li><strong>Plasmid map:</strong> Beautiful circular visualization</li>
                  </ul>
                </div>

                <div className="p-4 bg-gradient-to-r from-[#dae7da] to-[#cddccd] rounded-lg">
                  <h3 className="font-semibold text-[#5a6851] mb-2">🎯 Supported Features</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    <li>Gene prediction and functional annotation</li>
                    <li>Origin of replication (oriC) and transfer (oriT)</li>
                    <li>Mobile elements (transposons, insertion sequences)</li>
                    <li>Antibiotic resistance genes</li>
                    <li>ncRNA detection</li>
                    <li>Intergenic gene discovery</li>
                    <li>Enhanced annotation option with Uniprot</li>
                  </ul>
                </div>

                {/* Cold start explanation */}
                <div className="p-4 bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-200 rounded-lg">
                  <h3 className="font-semibold text-yellow-800 mb-2">⚡ About Server Performance</h3>
                  <p className="text-sm text-yellow-700">
                    The first analysis after idle time may take 30-60 seconds to start 
                    as the server wakes up. The system automatically retries if needed - just wait a moment!
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Results Section */}
          {results && results.files && results.files.length > 0 && (
            <div className="space-y-6">
              
              {/* Download Section */}
              <div className="bg-gradient-to-br from-white to-[#f7f9f7] shadow-lg rounded-lg p-6 border border-[#e1e8e1]">
                <h2 className="text-xl font-bold mb-4 bg-gradient-to-r from-[#4e5a4c] to-[#6b7966] bg-clip-text text-transparent">📁 Results</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {results.files.map((file, index) => (
                    <div key={index} className="text-center p-4 border border-[#d1ddd1] rounded-lg bg-gradient-to-br from-white to-[#f7f9f7] hover:from-[#f1f4f1] hover:to-[#e8efe8] transition-all duration-200">
                      <div className="text-3xl mb-2">{getFileType(file.name).split(' ')[0]}</div>
                      <div className="text-sm font-medium mb-2 text-gray-700">{getFileType(file.name).substring(2)}</div>
                      <div className="text-xs text-gray-500 mb-3">{(file.size / 1024).toFixed(1)} KB</div>
                      <button
                        onClick={() => downloadFile(file.relative_path)}
                        className="w-full bg-gradient-to-r from-[#6b7966] to-[#7a8577] text-white px-3 py-2 rounded hover:from-[#5d6a5b] hover:to-[#6b7966] text-sm transition-all duration-200"
                      >
                        📥 Download
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Plasmid Map Preview */}
              {results.files.find(f => f.name.endsWith('.png')) && (
                <div className="bg-gradient-to-br from-white to-[#f7f9f7] shadow-lg rounded-lg p-6 border border-[#e1e8e1]">
                  <h2 className="text-xl font-bold mb-4 bg-gradient-to-r from-[#4e5a4c] to-[#6b7966] bg-clip-text text-transparent">🎨 Plasmid Map Preview</h2>
                  <div className="text-center">
                    <img 
                      src={`${API_BASE}/api/file/${jobId}/${results.files.find(f => f.name.endsWith('.png')).relative_path}`}
                      alt="Plasmid Map"
                      className="max-w-full h-auto mx-auto border border-[#d1ddd1] rounded-lg shadow-md"
                      style={{ maxHeight: '600px' }}
                    />
                  </div>
                </div>
              )}

              {/* CSV Preview */}
              {csvData && (
                <div className="bg-gradient-to-br from-white to-[#f7f9f7] shadow-lg rounded-lg p-6 border border-[#e1e8e1]">
                  <h2 className="text-xl font-bold mb-4 bg-gradient-to-r from-[#4e5a4c] to-[#6b7966] bg-clip-text text-transparent">📊 Annotations Preview</h2>
                  
                  {/* Summary Statistics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-[#f1f4f1] to-[#e8efe8] p-4 rounded-lg text-center border border-[#d1ddd1]">
                      <div className="text-2xl font-bold text-[#4e5a4c]">{csvData.total_rows}</div>
                      <div className="text-sm text-[#6b7966]">Total Features</div>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg text-center border border-green-200">
                      <div className="text-2xl font-bold text-green-800">
                        {csvData.data.filter(row => row['feature type'] === 'CDS').length}
                      </div>
                      <div className="text-sm text-green-600">CDS Genes</div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg text-center border border-purple-200">
                      <div className="text-2xl font-bold text-purple-800">
                        {[...new Set(csvData.data.map(row => row.Category).filter(Boolean))].length}
                      </div>
                      <div className="text-sm text-purple-600">Categories</div>
                    </div>
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-lg text-center border border-orange-200">
                      <div className="text-2xl font-bold text-orange-800">
                        {csvData.data.filter(row => row['Intergenic_Flag'] === 1).length || 0}
                      </div>
                      <div className="text-sm text-orange-600">Intergenic Genes</div>
                    </div>
                  </div>
                  
                  {/* Scrollable Table */}
                  <div className="overflow-x-auto">
                    <div className="max-h-96 overflow-y-auto border border-[#d1ddd1] rounded-lg">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gradient-to-r from-[#f1f4f1] to-[#e8efe8] sticky top-0">
                          <tr>
                            {csvData.columns.map((col, index) => (
                              <th key={index} className="px-3 py-2 text-left font-medium text-[#4e5a4c] border-b border-[#d1ddd1]">
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {csvData.data.map((row, rowIndex) => (
                            <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gradient-to-r from-gray-50 to-[#fafbfa]'}>
                              {csvData.columns.map((col, colIndex) => (
                                <td key={colIndex} className="px-3 py-2 border-b border-gray-200 text-gray-700">
                                  {String(row[col] || '-')}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* No Results Message */}
          {results && (!results.files || results.files.length === 0) && (
            <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-200 p-6 rounded-lg">
              <h3 className="font-bold text-yellow-800">Analysis completed but no files found</h3>
              <p className="text-yellow-700">Check the backend console for any error messages.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Analysis;
